import { AnyExtension } from '@tiptap/core'
import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import Document from '@tiptap/extension-document'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import Italic from '@tiptap/extension-italic'
import { NotchLink } from './tiptap-link'
import '@/types/tiptap-ordered-list-augment'
import { CustomOrderedList } from './tiptap-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import TaskList from '@tiptap/extension-task-list'
import Text from '@tiptap/extension-text'
import { TextAlign } from '@tiptap/extension-text-align'
import Typography from '@tiptap/extension-typography'
import Underline from '@tiptap/extension-underline'
import Blockquote from '@tiptap/extension-blockquote'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import Strike from '@tiptap/extension-strike'
import ListKeymap from '@tiptap/extension-list-keymap'
import { Node as ProseMirrorNode } from 'prosemirror-model'
import { CustomListItem } from './tiptap-list-item'
import { TextDirection } from './tiptap-text-direction'
import { SmilieReplacer } from './tiptap-smilie-replacer'
import { CustomTaskItem } from './tiptap-task-item'
import TiptapPasteHandler from './tiptap-paste-handler'
import { VariablePlaceholder } from './tiptap-variable-placeholder'
import { CaretAnchor } from './tiptap-caret-anchor'
import { VariableWrapper } from './tiptap-variable-wrapper'
import { AssignCallout } from './tiptap-assign-callout'
import { EditorPlaceholder } from './tiptap-placeholder'
import { History } from './tiptap-history'
import Bookmark from './tiptap-bookmark'
import Iframe from './tiptap-iframe'
import { CustomMention, MentionPluginKey } from '@/util/tiptap-mention'
import type { CreateEditorParams } from './tiptap-editor-params'
import { editorContextFrom } from './tiptap-editor-params'

const ExtendedParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: element => {
          const value = element.getAttribute('class')
          return value && value !== 'null' ? value : null
        },
        renderHTML: attributes => {
          if (!attributes.class) {
            return {}
          }
          return {
            class: attributes.class
          }
        }
      }
    }
  }
})

/** Mention node in the schema; suggestion UI loads with the interactive chunk. */
function stubMentionExtension(): AnyExtension {
  return CustomMention.configure({
    HTMLAttributes: {
      class: 'mention'
    },
    suggestion: {
      pluginKey: MentionPluginKey,
      allow: () => false,
      items: () => [],
      render: () => ({
        onStart() {},
        onUpdate() {},
        onKeyDown: () => false,
        onExit() {}
      })
    }
  })
}

/**
 * Schema, keymaps, and paste handling without slash commands, variable autocomplete, or mention popups.
 * Matches the live editor schema once interactive extensions are merged in.
 */
export function buildCoreEditorExtensions(params: CreateEditorParams): AnyExtension[] {
  const { enableIframe = true, variableScopeId } = params
  const ctx = params.ctx || editorContextFrom(params)
  const extensions: AnyExtension[] = [
    BulletList,
    CustomOrderedList,
    CustomListItem,
    Underline,
    HardBreak,
    Text,
    Document,
    Italic,
    ExtendedParagraph,
    SmilieReplacer,
    VariablePlaceholder.configure({
      scopeId: variableScopeId || null
    }),
    VariableWrapper,
    AssignCallout,
    CaretAnchor,
    Typography,
    NotchLink,
    Bold,
    Heading,
    Strike,
    Blockquote,
    // Keeps the node so saved <hr> still parses; no input rules, so typing --- / *** / ___ stays text and the
    // canvas "�-" divider shortcut works. The content-scope slash item is the only way to insert one.
    HorizontalRule.extend({ addInputRules: () => [] }),
    TaskList,
    CustomTaskItem.configure({
      onReadOnlyChecked: (node: ProseMirrorNode, checked: boolean) => {
        return ctx.readOnlyTaskItemChecked ? ctx.readOnlyTaskItemChecked(node, checked) : false
      },
      nested: true
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'right', 'center', 'justify']
    }),
    TextDirection,
    History,
    ListKeymap,
    TiptapPasteHandler.configure({ ctx }),
    EditorPlaceholder,
    stubMentionExtension(),
    Bookmark
  ]

  if (enableIframe) {
    extensions.push(Iframe.configure({ ctx }))
  }

  return extensions
}
